import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Container, Row, Col, Card, Table, Form, Button, Spinner, Alert, Badge, Modal } from 'react-bootstrap'
import client from '../../api/client'
import HFSearchModal, { getPipelineLabel } from './HFSearchModal'

function HFModelList() {
  const navigate = useNavigate()
  const [models, setModels] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [deleteModal, setDeleteModal] = useState({ show: false, model: null })
  const [showSearchModal, setShowSearchModal] = useState(false)
  const [editLimitModal, setEditLimitModal] = useState({ show: false, model: null, newLimit: '' })

  useEffect(() => {
    fetchModels()
  }, [page])

  const fetchModels = async () => {
    setLoading(true)
    try {
      const response = await client.get('/admin/api/v1/hf-models', { params: { page, per_page: 20 } })
      setModels(response.data.models || [])
      setTotal(response.data.total || 0)
    } catch (err) {
      console.error('Error fetching HF models:', err)
      setError('Erreur lors du chargement des modèles HF')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteModal.model) return
    try {
      await client.delete(`/admin/api/v1/hf-models/${deleteModal.model.id}`)
      setSuccess(`Modèle "${deleteModal.model.name}" supprimé`)
      setDeleteModal({ show: false, model: null })
      fetchModels()
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      console.error('Error deleting model:', err)
      setError('Erreur lors de la suppression du modèle')
      setTimeout(() => setError(''), 3000)
    }
  }

  const handleToggleActive = async (model) => {
    try {
      await client.put(`/admin/api/v1/hf-models/${model.id}`, { active: !model.active })
      setSuccess(`Modèle "${model.name}" ${!model.active ? 'activé' : 'désactivé'}`)
      fetchModels()
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      console.error('Error toggling model active:', err)
      setError('Erreur lors de la modification du modèle')
      setTimeout(() => setError(''), 3000)
    }
  }

  const handleUpdateLimit = async () => {
    if (!editLimitModal.model) return
    try {
      await client.put(`/admin/api/v1/hf-models/${editLimitModal.model.id}`, {
        daily_limit: editLimitModal.newLimit === '' ? null : parseInt(editLimitModal.newLimit, 10)
      })
      setSuccess(`Limite journalière mise à jour pour "${editLimitModal.model.name}"`)
      setEditLimitModal({ show: false, model: null, newLimit: '' })
      fetchModels()
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      console.error('Error updating limit:', err)
      setError('Erreur lors de la mise à jour de la limite')
      setTimeout(() => setError(''), 3000)
    }
  }

  const handleModelAdded = (modelName) => {
    setSuccess(`Modèle ajouté: ${modelName}`)
    fetchModels()
    setTimeout(() => setSuccess(''), 3000)
  }

  const handleError = (msg) => {
    setError(msg)
    setTimeout(() => setError(''), 3000)
  }

  if (loading && models.length === 0) {
    return (
      <Container fluid>
        <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Chargement...</span>
          </Spinner>
        </div>
      </Container>
    )
  }

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <h2>Modèles Hugging Face</h2>
          <p className="text-muted">Gestion des modèles open-source du catalogue Hugging Face</p>
        </Col>
        <Col className="text-end">
          <Button variant="primary" onClick={() => setShowSearchModal(true)}>
            + Ajouter un modèle
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      <Card>
        <Card.Body>
          <Table responsive hover>
            <thead>
              <tr>
                <th>Nom</th>
                <th>HF Model ID</th>
                <th>Pipeline Tag</th>
                <th>Limite journalière</th>
                <th>Usage</th>
                <th>Votes</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {models.map((model) => (
                <tr key={model.id}>
                  <td>
                    <strong>{model.name}</strong>
                    {model.description && (
                      <>
                        <br />
                        <small className="text-muted">{model.description.substring(0, 50)}...</small>
                      </>
                    )}
                  </td>
                  <td><code className="small">{model.hf_model_id}</code></td>
                  <td><Badge bg="info">{getPipelineLabel(model.pipeline_tag)}</Badge></td>
                  <td>
                    <Badge
                      bg="secondary"
                      style={{ cursor: 'pointer' }}
                      onClick={() => setEditLimitModal({
                        show: true,
                        model,
                        newLimit: model.daily_limit !== null ? model.daily_limit.toString() : ''
                      })}
                    >
                      {model.daily_limit !== null ? model.daily_limit : 'Global (5)'}
                    </Badge>
                  </td>
                  <td className="text-center"><Badge bg="primary">{model.usage_count || 0}</Badge></td>
                  <td className="text-center">
                    <Badge bg={model.net_score > 0 ? 'success' : model.net_score < 0 ? 'danger' : 'secondary'}>
                      {model.net_score > 0 ? '+' : ''}{model.net_score || 0}
                    </Badge>
                  </td>
                  <td>
                    <Form.Check
                      type="switch"
                      id={`active-switch-${model.id}`}
                      checked={model.active}
                      onChange={() => handleToggleActive(model)}
                      label={model.active ? 'Actif' : 'Inactif'}
                    />
                  </td>
                  <td>
                    <div className="d-flex gap-1">
                      <Button variant="outline-info" size="sm" onClick={() => navigate(`/hf-models/${model.id}/stats`)}>
                        Stats
                      </Button>
                      <Button variant="outline-danger" size="sm" onClick={() => setDeleteModal({ show: true, model })}>
                        Supprimer
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {models.length === 0 && (
            <div className="text-center py-4 text-muted">
              Aucun modèle HF ajouté. Utilisez le bouton "Ajouter un modèle" pour en ajouter depuis le catalogue.
            </div>
          )}

          {total > 20 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <span className="text-muted">Page {page} sur {Math.ceil(total / 20)}</span>
              <div>
                <Button variant="outline-secondary" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)} className="me-2">
                  Précédent
                </Button>
                <Button variant="outline-secondary" size="sm" disabled={page >= Math.ceil(total / 20)} onClick={() => setPage(page + 1)}>
                  Suivant
                </Button>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal show={deleteModal.show} onHide={() => setDeleteModal({ show: false, model: null })}>
        <Modal.Header closeButton>
          <Modal.Title>Confirmer la suppression</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Êtes-vous sûr de vouloir supprimer le modèle "{deleteModal.model?.name}" ?
          Cette action supprimera également tous les votes et logs d'utilisation associés.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteModal({ show: false, model: null })}>Annuler</Button>
          <Button variant="danger" onClick={handleDelete}>Supprimer</Button>
        </Modal.Footer>
      </Modal>

      {/* Search/Add Modal */}
      <HFSearchModal
        show={showSearchModal}
        onHide={() => setShowSearchModal(false)}
        onModelAdded={handleModelAdded}
        onError={handleError}
      />

      {/* Edit Daily Limit Modal */}
      <Modal show={editLimitModal.show} onHide={() => setEditLimitModal({ show: false, model: null, newLimit: '' })}>
        <Modal.Header closeButton>
          <Modal.Title>Modifier la limite journalière</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Limite journalière pour "{editLimitModal.model?.name}"</Form.Label>
            <Form.Control
              type="number"
              placeholder="Laisser vide pour utiliser la limite globale (5)"
              value={editLimitModal.newLimit}
              onChange={(e) => setEditLimitModal({ ...editLimitModal, newLimit: e.target.value })}
              min="0"
            />
            <Form.Text className="text-muted">
              Laisser vide pour utiliser la limite globale par défaut (5 utilisations/jour)
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditLimitModal({ show: false, model: null, newLimit: '' })}>
            Annuler
          </Button>
          <Button variant="primary" onClick={handleUpdateLimit}>Enregistrer</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}

export default HFModelList
